import React, { useCallback } from 'react'
import { cva, VariantProps } from 'class-variance-authority'

import { filterPositive } from '@/lib/format'
import { cn } from '@/lib/utils'

import { Flex } from './Box'
import { Text } from './Text'

// 输入框字符统计后缀
export const SuffixMaxCounter: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    count: number
    max?: number
  }
> = ({ count, max, className, ...rest }) => {
  return (
    <div
      {...rest}
      className={cn(
        'color-placeholder px-1 text-xs',
        count > Number(max) ? 'text-destructive' : 'text-white/60',
        className
      )}
    >
      {count}
      {max != undefined && `/${max}`}
    </div>
  )
}

const inputVariants = cva(
  cn(
    'rounded-md border transition text-white shadow text-color-text-primary',
    'bg-secondary focus-within:bg-primary-pressed focus-within:input-focus-within',
    'data-[readonly=true]:text-border data-[readonly=true]:focus-within:bg-secondary',
    'data-[disabled=true]:text-border data-[disabled=true]:opacity-50 data-[disabled=true]:pointer-events-none'
  ),
  {
    variants: {
      size: {
        sm: cn(
          'px-2 gap-2',
          '[&_input]:py-1 [&_input]:text-sm xl:[&_input]:text-xl'
        ),
        md: cn(
          'h-8 xl:h-12 px-3 xl:px-4 gap-2',
          '[&_input]:text-sm xl:[&_input]:text-base',
          '[&_input]:py-2'
        ),
        lg: cn(
          'h-12 xl:h-16 px-4 gap-3',
          '[&_input]:text-sm xl:[&_input]:text-xl',
          '[&_input]:py-3'
        ),
        xl: 'h-18 px-4 [&_input]:py-4 text-xl gap-4'
      }
    },
    defaultVariants: {
      size: 'lg'
    }
  }
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  wrapperProps?: React.DOMAttributes<HTMLDivElement>
  wrapperClassName?: React.HTMLAttributes<HTMLDivElement>['className']
  prefixNode?: React.ReactNode
  suffixNode?: React.ReactNode
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    state?: boolean
  ) => void
  ignoreChangeType?: string[]
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      children,
      size,
      prefixNode,
      suffixNode,
      wrapperProps,
      wrapperClassName,
      onChange,
      ignoreChangeType,
      ...props
    },
    ref
  ) => {
    // 忽略的事件（中间状态的事件不做格式化）
    const ignoreType = ignoreChangeType ?? [
      'insertCompositionText',
      'deleteCompositionText'
    ]

    const onChangeProxy = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange && Array.isArray(ignoreType)) {
        const inputType = (event.nativeEvent as InputEvent)?.inputType
        const state = !ignoreType.includes(inputType)
        onChange(event, state)
      }
      // if (!ignoreType.includes(inputType)) {
      //   val = formatChange(event.target.value)
      // } else {
      //   val = event.target.value
      // }
    }
    return (
      <Flex
        {...wrapperProps}
        className={cn(
          'items-center',
          inputVariants({ className: wrapperClassName, size })
        )}
        data-readonly={props.readOnly}
        data-disabled={props.disabled}
      >
        {prefixNode}
        {children ?? (
          <input
            ref={ref}
            className={cn(
              'h-full w-0 flex-1 bg-transparent placeholder:text-color-text-tertiary',
              className
            )}
            autoComplete="off"
            onChange={onChangeProxy}
            {...props}
          />
        )}
        {suffixNode}
      </Flex>
    )
  }
)
Input.displayName = 'Input'

export const NumberInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof Input>
>(({ onChange, ...props }, ref) => {
  const handleChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const value = ev.target.value
      ev.target.value = filterPositive(value)
      onChange?.(ev)
    },
    [onChange]
  )

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      pattern="[0-9]+(\.[0-9]+)?"
      maxLength={18}
      onChange={handleChange}
      {...props}
    />
  )
})
NumberInput.displayName = 'NumberInput'

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  wrapperClassName?: React.HTMLAttributes<HTMLDivElement>['className']
  maxCounterClassName?: React.HTMLAttributes<HTMLDivElement>['className']
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, wrapperClassName, maxCounterClassName, onChange, ...props },
    ref
  ) => {
    return (
      <div
        className={cn(
          'items-center p-2 shadow xl:p-4',
          'rounded-md border text-color-text-primary transition',
          'focus-within:input-focus-within bg-secondary focus-within:bg-primary-pressed',
          'data-[readonly=true]:text-border data-[readonly=true]:focus-within:bg-secondary',
          'data-[disabled=true]:pointer-events-none data-[disabled=true]:text-border data-[disabled=true]:opacity-50',
          wrapperClassName
        )}
        data-readonly={props.readOnly}
        data-disabled={props.disabled}
      >
        <textarea
          ref={ref}
          rows={3}
          className={cn(
            'w-full resize-none bg-transparent text-sm placeholder:text-color-text-tertiary xl:text-base',
            className
          )}
          autoComplete="off"
          onChange={(ev) => {
            if (!ev.target.value.trim())
              ev.target.value = ev.target.value.trim()
            onChange?.(ev)
          }}
          {...props}
        />
        {props.maxLength && (
          <Flex className="justify-end">
            <Text
              variant="tertiary"
              className={cn(
                'text-sm !leading-none xl:text-base',
                maxCounterClassName
              )}
            >{`${props.value?.toString().length || 0}/${props.maxLength}`}</Text>
          </Flex>
        )}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'
